import { CARD_DISPLAY_MODE } from '../constants/learning-cards';

/**
 * Process text with reading pairs to create furigana display
 */
export function processTextWithReadings(text, readingPairs = []) {
    if (!text || !readingPairs || readingPairs.length === 0) {
        return [{ text, reading: null }];
    }

    const segments = [];
    let currentIndex = 0;

    // Sort reading pairs by length (longest first) to prioritize longer matches
    const sortedPairs = [...readingPairs].sort((a, b) => (b.text?.length || 0) - (a.text?.length || 0));

    while (currentIndex < text.length) {
        let foundMatch = null;
        let matchLength = 0;

        // Find the longest matching reading pair at current position
        for (const pair of sortedPairs) {
            if (!pair.text || !pair.reading) continue;

            const pairText = pair.text;
            if (text.substr(currentIndex, pairText.length) === pairText) {
                foundMatch = pair;
                matchLength = pairText.length;
                break;
            }
        }

        if (foundMatch) {
            // Add segment with reading
            segments.push({
                text: foundMatch.text,
                reading: foundMatch.reading
            });
            currentIndex += matchLength;
        } else {
            // Add single character without reading
            segments.push({
                text: text.charAt(currentIndex),
                reading: null
            });
            currentIndex++;
        }
    }

    return segments;
}

/**
 * Extract reading pairs from card side if available
 */
export function extractReadingPairs(cardSide) {
    if (!cardSide) {
        return [];
    }

    // Try both formats: camelCase and snake_case
    const readingPairs = cardSide.reading_pairs || cardSide.readingPairs;

    if (!readingPairs || !Array.isArray(readingPairs)) {
        return [];
    }

    return readingPairs
        .filter(pair => pair && pair.text && pair.reading) // Filter out invalid pairs
        .map(pair => ({
            text: pair.text.trim(),
            reading: pair.reading.trim()
        }))
        .filter(pair => pair.text && pair.reading); // Filter out empty pairs after trimming
}

/**
 * Extract front and back text from card data based on display mode
 */
export function extractCardTexts(card, groupCardSideTypes = [], displayMode = CARD_DISPLAY_MODE.SIDE_1_AS_FRONT) {
    if (!card) {
        return { frontText: '', backText: '', frontType: null, backType: null };
    }

    let firstSideText = '';
    let secondSideText = '';

    if (card.sides && Array.isArray(card.sides) && card.sides.length >= 2) {
        firstSideText = card.sides[0]?.text || '';
        secondSideText = card.sides[1]?.text || '';
    } else {
        firstSideText = card.front_text || card.frontText || '';
        secondSideText = card.back_text || card.backText || '';
    }

    const firstSideType = groupCardSideTypes[0] || 'CARD_SIDE_NULL';
    const secondSideType = groupCardSideTypes[1] || 'CARD_SIDE_NULL';

    if (displayMode === CARD_DISPLAY_MODE.SIDE_2_AS_FRONT) {
        return {
            frontText: secondSideText,
            backText: firstSideText,
            frontType: secondSideType,
            backType: firstSideType
        };
    }

    return {
        frontText: firstSideText,
        backText: secondSideText,
        frontType: firstSideType,
        backType: secondSideType
    };
}

/**
 * Normalize card object to ensure consistent format for frontend
 */
export function normalizeCard(card, groupCardSideTypes = [], displayMode = CARD_DISPLAY_MODE.SIDE_1_AS_FRONT) {
    if (!card) {
        return null;
    }

    const { frontText, backText, frontType, backType } = extractCardTexts(card, groupCardSideTypes, displayMode);

    return {
        id: card.id,
        groupId: card.group_id || card.groupId,
        frontText,
        backText,
        frontType,
        backType,
        createdAt: card.created_at || card.createdAt,
        sides: card.sides || [],
        front_text: frontText,
        back_text: backText
    };
}

/**
 * Normalize group object to ensure consistent format for frontend
 */
export function normalizeGroup(group) {
    if (!group) {
        return null;
    }

    return {
        id: group.id,
        ownerId: group.owner_id || group.ownerId,
        name: group.name,
        description: group.description || '',
        createdAt: group.created_at || group.createdAt,
        visibility: group.visibility || 'PRIVATE',
        cardSideTypes: group.card_side_types || group.cardSideTypes || []
    };
}

/**
 * Convert frontend card data to API format
 */
export function formatCardForAPI(cardData) {
    if (cardData.sides_text && Array.isArray(cardData.sides_text)) {
        return {
            group_id: cardData.group_id,
            sides_text: cardData.sides_text
        };
    }

    if (cardData.front_text && cardData.back_text) {
        return {
            group_id: cardData.group_id,
            sides_text: [cardData.front_text, cardData.back_text]
        };
    }

    throw new Error('Invalid card data format');
}

/**
 * Convert frontend group data to API format
 */
export function formatGroupForAPI(groupData) {
    return {
        group_name: groupData.group_name,
        description: groupData.description || '',
        visibility: groupData.visibility || 'PRIVATE',
        card_side_types: groupData.card_side_types || []
    };
}

/**
 * Determine if a card side should use Japanese formatting
 */
export function isJapaneseSide(sideType) {
    return sideType === 'CARD_SIDE_JAPANESE';
}